import {Modal, Select} from '@mantine/core';
import {Table} from 'src/design/atoms/Table';
import {Alert} from 'src/design/atoms/Alert';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React, {useEffect, useState} from 'react';
import type {PersonnelBadgeSimpleResponse} from 'src/api/ProactApiClient';
import {useGetPersonnelBadgeQuery} from 'src/redux/queries/personnelBadges';
import 'src/utilities/extensions/stringExtensions';
import {BadgeArtifactPreview} from './BadgeArtifactPreview';
import {diffBadgeSnapshots} from './badgeSnapshotDiff';
import {useBadgeArtifactHtml} from './useBadgeArtifactHtml';

export interface BadgeCompareModalProps {
  opened: boolean;
  personnelId: number;
  history: PersonnelBadgeSimpleResponse[];
  initialLeftBadgeId?: number;
  initialRightBadgeId?: number;
  onClose: () => void;
}

const versionLabel = (badge: PersonnelBadgeSimpleResponse) =>
  `v${badge.version} — ${String(badge.generatedAt).toLongDate()}`;

export const BadgeCompareModal: React.FC<BadgeCompareModalProps> = ({
  opened,
  personnelId,
  history,
  initialLeftBadgeId,
  initialRightBadgeId,
  onClose,
}) => {
  const [leftBadgeId, setLeftBadgeId] = useState<number | undefined>(
    initialLeftBadgeId
  );
  const [rightBadgeId, setRightBadgeId] = useState<number | undefined>(
    initialRightBadgeId
  );

  useEffect(() => {
    if (!opened) return;
    setLeftBadgeId(initialLeftBadgeId ?? history[1]?.id ?? history[0]?.id);
    setRightBadgeId(initialRightBadgeId ?? history[0]?.id);
    // Only re-seed when the modal is (re)opened with new defaults.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, initialLeftBadgeId, initialRightBadgeId]);

  const {data: leftDetail, isFetching: isLeftFetching} =
    useGetPersonnelBadgeQuery(
      {id: personnelId, badgeId: leftBadgeId as number},
      {skip: leftBadgeId === undefined}
    );
  const {data: rightDetail, isFetching: isRightFetching} =
    useGetPersonnelBadgeQuery(
      {id: personnelId, badgeId: rightBadgeId as number},
      {skip: rightBadgeId === undefined}
    );

  const leftArtifact = useBadgeArtifactHtml(leftDetail?.downloadUrl);
  const rightArtifact = useBadgeArtifactHtml(rightDetail?.downloadUrl);

  const diffEntries = diffBadgeSnapshots(
    leftDetail?.dataSnapshot,
    rightDetail?.dataSnapshot
  );

  const options = history.map(badge => ({
    value: String(badge.id),
    label: versionLabel(badge),
  }));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Compare Badge Versions"
      size="xl"
      styles={{title: {fontWeight: 700}}}
      data-testid="badge-compare-modal">
      <Stack gap="md">
        <SimpleGrid cols={2}>
          <Select
            label="Version A"
            data={options}
            value={leftBadgeId !== undefined ? String(leftBadgeId) : null}
            onChange={value => setLeftBadgeId(value ? Number(value) : undefined)}
            placeholder="Select a version"
          />
          <Select
            label="Version B"
            data={options}
            value={rightBadgeId !== undefined ? String(rightBadgeId) : null}
            onChange={value =>
              setRightBadgeId(value ? Number(value) : undefined)
            }
            placeholder="Select a version"
          />
        </SimpleGrid>

        <SimpleGrid cols={2}>
          <BadgeArtifactPreview
            html={leftArtifact.html}
            isLoading={isLeftFetching || leftArtifact.isLoading}
            error={leftArtifact.error}
            title="Version A preview"
            hidePrintButton
          />
          <BadgeArtifactPreview
            html={rightArtifact.html}
            isLoading={isRightFetching || rightArtifact.isLoading}
            error={rightArtifact.error}
            title="Version B preview"
            hidePrintButton
          />
        </SimpleGrid>

        <Divider label="Differences" labelPosition="left" />

        {diffEntries.length === 0 ? (
          <Alert variant="light" color="blue">
            No differences found between these two versions.
          </Alert>
        ) : (
          <Table data-testid="badge-compare-diff-table">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Field</Table.Th>
                <Table.Th>Version A</Table.Th>
                <Table.Th>Version B</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {diffEntries.map(entry => (
                <Table.Tr key={entry.field}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {entry.label}
                    </Text>
                  </Table.Td>
                  <Table.Td>{entry.before}</Table.Td>
                  <Table.Td>{entry.after}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        <Group justify="flex-end">
          <Text size="xs" c="dimmed">
            Print at 100% / Actual Size — do not use Fit to Page.
          </Text>
        </Group>
      </Stack>
    </Modal>
  );
};
